/**
 * My preferences — the signed-in admin's own settings
 * (docs/plans/ADMIN_SETTINGS_SCOPE_2026_09_29.md, WS-F).
 *
 * Profile (name, timezone), how you appear on support replies, Slack (your
 * @handle for roadmap mentions, and a one-click DM link), which optional team
 * notifications you receive, your saved Alert Center views, and where you're
 * signed in (Sessions).
 *
 * Endpoints (routers/admin_me.py): GET/PUT /api/admin/me/profile,
 * PUT /api/admin/me/notifications, POST/DELETE /api/admin/me/slack/link,
 * PUT /api/admin/me/alert-views. Sessions (routers/admin_sessions.py):
 * GET /api/admin/me/sessions, DELETE /api/admin/me/sessions/:id,
 * POST /api/admin/me/sessions/revoke-others.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BellOff, Bookmark, LogOut, Mail, MessageSquare, Monitor, Save, Trash2, UserCog } from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { adminFetch, adminMutate } from '../../../utils/adminApi';
import { errorMessage } from '../settings/settingsLogic';
import { MeProfile, SavedAlertView, profileDiff, timezoneOptions } from './meLogic';
import { SessionsList } from './SessionsList';
import { SessionsResponse, otherSessionCount } from './sessionsLogic';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 20_000, refetchOnWindowFocus: false } },
});

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode; id?: string }> = ({ title, icon, children, id }) => (
  <section id={id} className="mt-6 scroll-mt-20 rounded-lg border border-gray-200 bg-white p-4">
    <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">{icon} {title}</h2>
    <div className="mt-3 space-y-3">{children}</div>
  </section>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="block text-sm">
    <span className="block font-medium text-gray-800">{label}</span>
    {hint && <span className="block text-xs text-gray-500">{hint}</span>}
    <span className="mt-1 block">{children}</span>
  </label>
);

const input = 'w-full max-w-md rounded-md border border-gray-300 px-2 py-1.5 text-sm';

const SessionsSection: React.FC = () => {
  const sessions = useQuery({
    queryKey: ['admin', 'me', 'sessions'],
    queryFn: () => adminFetch<SessionsResponse>('/api/admin/me/sessions'),
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const location = useLocation();

  // Settings → Security links here as /admin/me#sessions.
  useEffect(() => {
    if (location.hash === '#sessions' && sessions.data) {
      document.getElementById('sessions')?.scrollIntoView({ block: 'start' });
    }
  }, [location.hash, sessions.data]);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      setStatus({ ok: true, text: ok });
    } catch (err) {
      setStatus({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
      await sessions.refetch();
    }
  };

  const list = sessions.data?.sessions ?? [];
  const others = otherSessionCount(list);
  const maxHours = sessions.data?.max_hours ?? 8;

  return (
    <Section title="Sessions" icon={<Monitor className="h-4 w-4" />} id="sessions">
      <p className="text-xs text-gray-500">
        Where you're signed in to the portal. A session ends {maxHours} hours after sign-in
        (<Link to="/admin/settings/security" className="text-[#1F6C4F] hover:underline">Settings → Security</Link>).
      </p>
      {sessions.isLoading && <p className="text-xs text-gray-500">Loading…</p>}
      {sessions.error && <p className="text-xs text-red-600">{errorMessage(sessions.error)}</p>}
      {sessions.data && (
        <SessionsList sessions={list} maxHours={maxHours} busy={busy}
          onEnd={sess => run(() => adminMutate(`/api/admin/me/sessions/${sess.id}`, 'DELETE'), 'Signed out.')} />
      )}
      <div className="flex flex-wrap items-center gap-3">
        {others > 0 && (
          <button type="button" disabled={busy}
            onClick={() => {
              if (window.confirm(`Sign out ${others} other session${others === 1 ? '' : 's'}? This device stays signed in.`)) {
                run(() => adminMutate('/api/admin/me/sessions/revoke-others', 'POST'), 'Other sessions signed out.');
              }
            }}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <LogOut className="h-3 w-3" /> Sign out other sessions
          </button>
        )}
        {status && <span className={`text-xs ${status.ok ? 'text-green-700' : 'text-red-600'}`} role="status">{status.text}</span>}
      </div>
    </Section>
  );
};

const MeInner: React.FC = () => {
  const profile = useQuery({
    queryKey: ['admin', 'me', 'profile'],
    queryFn: () => adminFetch<MeProfile>('/api/admin/me/profile'),
  });
  const [draft, setDraft] = useState<Partial<MeProfile>>({});
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const zones = useMemo(() => timezoneOptions(), []);

  useEffect(() => {
    if (profile.data) setDraft({});
  }, [profile.data]);

  const value = <K extends keyof MeProfile>(k: K): MeProfile[K] | undefined =>
    (k in draft ? draft[k] : profile.data?.[k]) as MeProfile[K] | undefined;
  const set = <K extends keyof MeProfile>(k: K, v: MeProfile[K]) => setDraft(d => ({ ...d, [k]: v }));

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      setStatus({ ok: true, text: ok });
      await profile.refetch();
    } catch (err) {
      setStatus({ ok: false, text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = () => {
    if (!profile.data) return;
    const changes = profileDiff(profile.data, draft);
    if (!Object.keys(changes).length) {
      setStatus({ ok: true, text: 'Nothing to save.' });
      return;
    }
    act(() => adminMutate('/api/admin/me/profile', 'PUT', changes), 'Saved.');
  };

  const toggleMute = (key: string, muted: boolean) => {
    const current = new Set(profile.data?.muted_events ?? []);
    if (muted) current.add(key); else current.delete(key);
    act(() => adminMutate('/api/admin/me/notifications', 'PUT', { muted_events: Array.from(current) }),
      muted ? 'Muted.' : 'You will receive it again.');
  };

  const removeView = (view: SavedAlertView) => {
    const views = (profile.data?.saved_alert_views ?? []).filter(v => v.name !== view.name);
    act(() => adminMutate('/api/admin/me/alert-views', 'PUT', { views }), 'Removed.');
  };

  if (profile.isLoading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (profile.error || !profile.data) {
    return <div className="m-6 rounded-md bg-red-50 p-3 text-sm text-red-700">{errorMessage(profile.error)}</div>;
  }
  const p = profile.data;

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
        <UserCog className="h-5 w-5" style={{ color: '#1F6C4F' }} /> My preferences
      </h1>
      <p className="mt-1 text-sm text-gray-500">{p.email} · {p.role.replace('_', ' ')}</p>

      <Section title="Profile" icon={<UserCog className="h-4 w-4" />}>
        <Field label="Name">
          <input className={input} value={(value('full_name') as string) ?? ''} onChange={e => set('full_name', e.target.value)} />
        </Field>
        <Field label="Timezone" hint="Used for times in scheduled emails like digests.">
          <select className={input} value={(value('timezone') as string) ?? ''} onChange={e => set('timezone', e.target.value)}>
            {!zones.includes(p.timezone ?? '') && p.timezone && <option value={p.timezone}>{p.timezone}</option>}
            {zones.map(z => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Support replies" icon={<Mail className="h-4 w-4" />}>
        <Field label="From name" hint='What users see on your ticket replies, e.g. "Matt at Coach Cedar". Blank uses the team default.'>
          <input className={input} value={(value('support_from_display_name') as string) ?? ''}
            onChange={e => set('support_from_display_name', e.target.value)} />
        </Field>
        <Field label="From address" hint="Optional, must be @coachcedar.com. Blank uses the team default.">
          <input className={input} value={(value('support_from_email') as string) ?? ''}
            onChange={e => set('support_from_email', e.target.value)} />
        </Field>
        <Field label="Signature (HTML)" hint="Added under your replies. No scripts.">
          <textarea className={`${input} h-24 font-mono text-xs`} value={(value('support_signature_html') as string) ?? ''}
            onChange={e => set('support_signature_html', e.target.value)} />
        </Field>
      </Section>

      <Section title="Slack" icon={<MessageSquare className="h-4 w-4" />}>
        <Field label="Handle" hint="Your @name in roadmap comments, e.g. paul. Lowercase.">
          <input className={input} value={(value('slack_handle') as string) ?? ''} onChange={e => set('slack_handle', e.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className={p.slack_dm_connected ? 'text-green-700' : 'text-gray-500'}>
            {p.slack_dm_connected ? 'Direct messages from the Cedar bot are on.' : 'The Cedar bot can’t DM you yet.'}
          </span>
          {p.slack_dm_connected ? (
            <button type="button" disabled={busy} onClick={() => act(() => adminMutate('/api/admin/me/slack/link', 'DELETE'), 'Slack DMs off.')}
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">Turn off</button>
          ) : (
            <button type="button" disabled={busy} onClick={() => act(() => adminMutate('/api/admin/me/slack/link', 'POST'), 'Slack connected.')}
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">Connect by my email</button>
          )}
        </div>
      </Section>

      <div className="mt-4 flex items-center gap-3">
        <button type="button" disabled={busy} onClick={saveProfile}
          className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#1F6C4F' }}>
          <Save className="h-4 w-4" /> Save changes
        </button>
        {status && <span className={`text-sm ${status.ok ? 'text-green-700' : 'text-red-600'}`} role="status">{status.text}</span>}
      </div>

      <SessionsSection />

      <Section title="Team notifications I receive" icon={<BellOff className="h-4 w-4" />}>
        <p className="text-xs text-gray-500">
          Applies where a notification is sent to you as a team member (not to a shared address).
          Recipients are set in <Link to="/admin/settings/notifications" className="text-[#1F6C4F] hover:underline">Settings → Notifications</Link>.
        </p>
        {p.events.map(ev => {
          const muted = p.muted_events.includes(ev.key);
          return (
            <label key={ev.key} className="flex items-center justify-between gap-2 text-sm">
              <span>{ev.label}{ev.required && <span className="ml-2 text-xs text-gray-400">always on</span>}</span>
              <input type="checkbox" checked={!muted} disabled={busy || ev.required}
                onChange={e => toggleMute(ev.key, !e.target.checked)} aria-label={`Receive ${ev.label}`} />
            </label>
          );
        })}
      </Section>

      <Section title="Saved alert views" icon={<Bookmark className="h-4 w-4" />}>
        {p.saved_alert_views.length === 0 && (
          <p className="text-xs text-gray-500">None yet. Save one from the Alerts page with “Save view”.</p>
        )}
        {p.saved_alert_views.map(v => (
          <div key={v.name} className="flex items-center justify-between gap-2 text-sm">
            <Link to={`/admin/alerts?${v.query}`} className="text-[#1F6C4F] hover:underline">{v.name}</Link>
            <button type="button" disabled={busy} onClick={() => removeView(v)} aria-label={`Remove ${v.name}`}
              className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </Section>
    </div>
  );
};

const MyPreferences: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <MeInner />
  </QueryClientProvider>
);

export default MyPreferences;
